<div v-if="programme" class="programme-detail-container text-dark p-3">

  <!-- Programme Header Card -->
  <div class="card rounded-4 p-4 mb-4 shadow-sm border-primary text-xs">
    <div class="d-flex flex-column flex-lg-row align-items-start gap-3">

      <!-- Icon -->
      <div class="rounded-3 d-flex align-items-center justify-content-center bg-primary p-3">
        <i :class="programme.icon || 'bi bi-folder'" class="text-dark fs-6"></i>
      </div>

      <!-- Header Info -->
      <div class="flex-grow-1">
        <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
          <h4 class="fw-semibold text-white mb-0 text-xs">{{ programme.name }}</h4>
          <span class="badge rounded-pill text-xs" :class="badgeClass(programme.status)">
            {{ programme.status }}
          </span>
        </div>
        <p class="text-white-50 mb-1 text-xs">{{ programme.description }}</p>
        <p class="text-white-50 mb-0 text-xs">
          <span class="fw-light">Deadline:</span> {{ programme.deadline }}
        </p>
      </div>

      <!-- Stats -->
      <div class="d-flex flex-column flex-sm-row gap-3 align-items-center">
        <div class="text-center h-100">
          <div class="d-flex align-items-center gap-1">
            <div class="fw-semibold text-white programme-text-lg text-xs">{{ programme.current_points }}</div>
            <div class="text-white-50 text-xs">of {{ programme.total_points }} points</div>
          </div>
          <div class="text-primary text-xs mt-1">Target: {{ programme.target }}</div>
        </div>
        <div class="programme-stats-min-width d-flex flex-column gap-2">
          <div class="d-flex align-items-center justify-content-between">
            <span class="text-white-50 text-xs">Workflow Progress</span>
            <span class="text-white text-xs fw-medium">{{ programme.workflow_completed }}/{{ programme.workflow_total }}</span>
          </div>
          <div class="progress bg-secondary rounded-pill">
            <div
              class="progress-bar bg-primary rounded-pill"
              role="progressbar"
              :style="`width: ${programme.progressPercent}%`"
              :aria-valuenow="programme.progressPercent"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <p class="text-white-50 text-xs m-0">{{ programme.workflow_completed }} of {{ programme.workflow_total }} steps completed</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Workflow Section -->
  <div class="row g-3">
    <!-- Steps Column -->
    <div class="col-lg-8">
      <div class="card border-primary rounded-4 p-4 mb-3 shadow-sm text-xs">
        <h4 class="fw-semibold text-white mb-1 text-xs">Document Collection Workflow</h4>
        <p class="text-white-50 text-xs mb-0">Follow these steps to complete {{ programme.name }} documentation</p>
      </div>

      <!-- Steps -->
      <div class="ps-2">
        <div v-for="(step, index) in programme.steps" :key="step.id" class="d-flex gap-3 mb-3">

          <!-- Step Indicator -->
          <div class="d-flex flex-column align-items-center">
            <div class="rounded-circle d-flex align-items-center justify-content-center bg-secondary programme-step-indicator"
              :class="stepDotClass(step.status)">
              <i v-if="step.status === 'completed'" class="bi bi-check fs-5 text-dark"></i>
              <i v-else class="bi bi-clock fs-5 text-dark"></i>
            </div>
            <div
              v-if="index < programme.steps.length - 1"
              class="flex-grow-1 rounded w-1"
              :class="step.status === 'completed' ? 'bg-success' : 'bg-secondary'"
              style="width: 0.2rem;"
            ></div>

          </div>

          <!-- Step Content -->
          <div class="flex-grow-1 pb-2">
            <div class="card rounded-3 p-3 shadow-sm border-primary text-xs">
              <div class="d-flex flex-column flex-sm-row justify-content-between gap-2">

                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-1 mb-1">
                    <span class="text-xs fw-medium text-white-50">Step {{ index + 1 }}</span>
                    <span class="badge rounded-pill text-xs" :class="stepBadgeClass(step.status)">{{ readableStatus(step.status) }}</span>
                  </div>
                  <h4 class="h6 fw-semibold text-white mb-1 text-xs">{{ step.title }}</h4>
                  <p class="text-xs fw-light text-white-50 mb-2">{{ step.description }}</p>

                  <!-- Required Documents -->
                  <div v-if="step.required_documents && step.required_documents.length" class="mb-2">
                    <p class="text-xs fw-medium text-white-50 mb-1">Required Documents:</p>
                    <ul class="list-unstyled mb-0">
                      <li v-for="doc in step.required_documents" :key="doc" class="text-xs text-white d-flex align-items-center gap-1 mb-1">
                        <i class="bi bi-chevron-right text-white-50"></i>{{ doc }}
                      </li>
                    </ul>
                  </div>

                  <!-- Assignee -->
                  <div v-if="step.owner_name" class="d-flex align-items-center gap-1">
                    <div class="rounded-circle bg-primary bg-opacity-25 text-dark d-flex align-items-center justify-content-center programme-owner-avatar text-xs">
                      <span class="fw-medium">{{ step.owner_initials }}</span>
                    </div>
                    <span class="text-xs fw-light text-white-50">{{ step.owner_name }} • {{ step.owner_role }}</span>
                  </div>
                </div>

                <!-- Due Date & Actions -->
                <div class="d-flex flex-column align-items-end gap-1">
                  <span class="text-xs text-white-50">Due: {{ step.due }}</span>
                  <button v-if="step.status !== 'completed'" class="btn btn-primary btn-sm rounded-3 text-xs">
                    {{ step.status === 'in_progress' ? 'Complete' : 'Start' }}
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Sidebar Column -->
    <div class="col-lg-4">
      <!-- Alerts Card -->
      <div class="card rounded-4 mb-3 border-primary text-xs">
        <div class="d-flex align-items-center justify-content-between p-2 border-bottom border-secondary">
          <div>
            <h3 class="fw-semibold text-white mb-1 text-xs">Alerts & Reminders</h3>
            <p class="text-xs text-white-50 mb-0">{{ alert_steps.length }} Unread Notification{{ alert_steps.length !== 1 ? 's' : '' }}</p>
          </div>
          <div class="text-center py-1 px-3 rounded-pill bg-danger">
            <p class="fw-bold text-dark mb-0 text-xs">{{ overdue_steps.length }} Overdue</p>
          </div>
        </div>
        
        <!-- Alerts List -->
        <div class="p-2">
          <div v-if="alert_steps.length === 0" class="text-center py-3">
            <i class="bi bi-check-circle text-success fs-4"></i>
            <p class="text-white-50 text-xs mb-0 mt-2">No pending alerts</p>
          </div>
          
          <div v-for="step in visible_alert_steps" :key="step.id" class="alert d-flex align-items-start gap-2 mb-2" 
               :class="step.status === 'overdue' ? 'alert-danger' : 'alert-warning'"
               role="alert">
            <div class="flex-grow-1">
              <div class="d-flex align-items-center gap-2 mb-1">
                <i class="bi" :class="step.status === 'overdue' ? 'bi-exclamation-triangle-fill' : 'bi-bell-fill'"></i>
                <strong class="text-xs">{{ step.title }}</strong>
              </div>
              <p class="mb-1 text-xs">{{ step.description }}</p>
              <small class="text-xs">
                <i class="bi bi-calendar-event"></i> Due: {{ step.due }}
              </small>
            </div>
            <button type="button" class="btn-close btn-close btn-sm"
                    @click="dismiss_alert(step.id)"
                    aria-label="Close"></button>
          </div>
          
          <div v-if="has_more_alerts && !show_all_alerts" class="text-center mt-2">
            <button class="btn btn-outline-secondary btn-sm rounded-3 text-xs fw-semibold" @click="toggleViewMore">
              View More ({{ alert_steps.length - 3 }} more)
            </button>
          </div>

          <div v-if="show_all_alerts && has_more_alerts" class="text-center mt-2">
            <button class="btn btn-outline-secondary btn-sm rounded-3 text-xs fw-semibold" @click="toggleViewMore">
              View Less
            </button>
          </div>
        </div>
      </div>

      <!-- Team Members Card -->
      <div class="card rounded-4 shadow-sm p-2 border-primary text-xs">
        <div class="p-2 border-bottom border-secondary">
          <h3 class="fw-semibold text-white mb-1 text-xs">Key Contacts</h3>
          <p class="text-xs text-white-50 mb-0">Responsible individual for this element</p>
        </div>
        <div>
          <div v-for="step in programme.steps" :key="step.id" class="rounded-4 mb-2 bg-secondary bg-opacity-10 p-2 text-xs">

            <!-- Header -->
            <div class="d-flex align-items-start gap-2">
              <!-- Avatar -->
              <div class="d-flex align-items-center justify-content-center rounded-circle bg-primary text-dark fw-semibold key-contact-initials text-xs">
                {{ step.owner_initials }}
              </div>

              <!-- Name + Meta -->
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-1 flex-wrap">
                  <div class="h6 mb-0 text-white fw-semibold text-xs">{{ step.owner_name }}</div>
                </div>
                <div class="text-white-50 text-xs mt-1">{{ step.owner_role }}</div>
                <div v-if="step.owner_role_desc" class="text-white-50 text-xs fw-medium mt-1">{{ step.owner_role_desc }}</div>
              </div>
            </div>

            <!-- Contact Pills -->
            <div class="mt-2 d-grid gap-1">
              <a v-if="step.owner_email" :href="`mailto:${step.owner_email}`">
                <div class="btn btn-light border rounded-3 d-inline-flex align-items-center gap-1 px-2 py-1 text-start text-xs">
                  <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border">
                    <i class="far fa-envelope text-muted"></i>
                  </span>
                  <span class="text-white fw-medium text-xs">{{ step.owner_email }}</span>
                </div>
              </a>
              <a v-if="step.owner_number" :href="`tel:${step.owner_number}`">
                <div class="btn btn-light border rounded-3 d-inline-flex align-items-center gap-1 px-2 py-1 text-start text-xs">
                  <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border">
                    <i class="fas fa-phone text-muted"></i>
                  </span>
                  <span class="text-white fw-medium text-xs">{{ step.owner_number }}</span>
                </div>
              </a>
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</div>

<div v-else class="text-white text-center py-5 text-xs">
  <p>Programme not found</p>
</div>
